import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUser, FiLock, FiBell, FiShield, FiSave, FiLoader } from 'react-icons/fi';
import PageHeader from '../components/ui/PageHeader';
import ActionButton from '../components/ui/ActionButton';
import { dataFetch } from '../api';

const Toggle = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-[#0f172a]' : 'bg-slate-200'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
  </button>
);

const Settings = () => {
  const [settings, setSettings] = useState({
    companyName: 'Acme Corporation',
    adminEmail: 'admin@acme.com',
    defaultTimezone: 'UTC',
    emailNotifications: true,
    slackAlerts: false,
    autoReminders: true,
    ssoEnabled: false,
    mfaRequired: true,
    autoEnrollNewHires: true,
    anonymizeReports: false,
    defaultLanguage: 'English'
  });
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      const res = await dataFetch('read/system_settings');
      if (res?.status === 'success' && res.data && res.data.length > 0) {
        const row = res.data[0];
        setRecordId(row.id);
        setSettings({
          companyName: row.companyName ?? 'Acme Corporation',
          adminEmail: row.adminEmail ?? 'admin@acme.com',
          defaultTimezone: row.defaultTimezone ?? 'UTC',
          emailNotifications: Boolean(row.emailNotifications ?? true),
          slackAlerts: Boolean(row.slackAlerts ?? false),
          autoReminders: Boolean(row.autoReminders ?? true),
          ssoEnabled: Boolean(row.ssoEnabled ?? false),
          mfaRequired: Boolean(row.mfaRequired ?? true),
          autoEnrollNewHires: Boolean(row.autoEnrollNewHires ?? true),
          anonymizeReports: Boolean(row.anonymizeReports ?? false),
          defaultLanguage: row.defaultLanguage ?? 'English'
        });
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...settings,
      emailNotifications: settings.emailNotifications ? 1 : 0,
      slackAlerts: settings.slackAlerts ? 1 : 0,
      autoReminders: settings.autoReminders ? 1 : 0,
      ssoEnabled: settings.ssoEnabled ? 1 : 0,
      mfaRequired: settings.mfaRequired ? 1 : 0,
      autoEnrollNewHires: settings.autoEnrollNewHires ? 1 : 0,
      anonymizeReports: settings.anonymizeReports ? 1 : 0,
      user_id: localStorage.getItem('user_id')
    };

    if (recordId) {
      await dataFetch(`update/system_settings/${recordId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    } else {
      const res = await dataFetch('create/system_settings', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (res?.data?.id) {
        setRecordId(res.data.id);
      }
    }
    setSaving(false);
  };

  const sections = [
    {
      icon: FiUser,
      title: 'Organization',
      items: [
        { label: 'Company Name', type: 'input', key: 'companyName' },
        { label: 'Admin Email',  type: 'input', key: 'adminEmail'   },
        { label: 'Default Timezone', type: 'select', key: 'defaultTimezone', options: ['UTC','America/New_York','EST','Europe/London'] },
      ],
    },
    {
      icon: FiBell,
      title: 'Notifications',
      items: [
        { label: 'Email Notifications',  type: 'toggle', key: 'emailNotifications' },
        { label: 'Slack Alerts',         type: 'toggle', key: 'slackAlerts'         },
        { label: 'Automated Reminders',  type: 'toggle', key: 'autoReminders'       },
      ],
    },
    {
      icon: FiLock,
      title: 'Security & Access',
      items: [
        { label: 'SSO / SAML Integration',  type: 'toggle', key: 'ssoEnabled'        },
        { label: 'Require MFA for Admins',  type: 'toggle', key: 'mfaRequired'       },
        { label: 'Anonymize Reports',       type: 'toggle', key: 'anonymizeReports'  },
      ],
    },
    {
      icon: FiShield,
      title: 'Training Defaults',
      items: [
        { label: 'Auto-enroll New Hires',     type: 'toggle', key: 'autoEnrollNewHires' },
        { label: 'Default Training Language', type: 'select', key: 'defaultLanguage', options: ['English','Spanish','French'] },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SafeIcon icon={FiLoader} className="animate-spin text-4xl text-slate-300" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <PageHeader 
        eyebrow="System" 
        title="Settings" 
        description="Configure platform preferences, security policies and automation defaults." 
      />

      <div className="max-w-2xl space-y-5 pb-10">
        {sections.map(section => (
          <div key={section.title} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
              <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={section.icon} className="text-slate-600 text-sm" />
              </div>
              <h3 className="text-sm font-semibold text-slate-800">{section.title}</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {section.items.map(item => (
                <div key={item.label} className="flex items-center justify-between px-5 py-3.5">
                  <label className="text-sm font-medium text-slate-700">{item.label}</label>
                  {item.type === 'toggle' && (
                    <Toggle 
                      enabled={settings[item.key]} 
                      onToggle={() => handleChange(item.key, !settings[item.key])} 
                    />
                  )}
                  {item.type === 'input' && (
                    <input 
                      value={settings[item.key] || ''} 
                      onChange={(e) => handleChange(item.key, e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 w-52 text-slate-700 focus:outline-none focus:border-slate-400 bg-slate-50 focus:bg-white transition-colors" 
                    />
                  )}
                  {item.type === 'select' && (
                    <select 
                      value={settings[item.key] || ''} 
                      onChange={(e) => handleChange(item.key, e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 w-52 text-slate-700 focus:outline-none focus:border-slate-400 bg-white transition-colors"
                    >
                      {item.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-4">
          <ActionButton 
            icon={saving ? FiLoader : FiSave} 
            label={saving ? "Saving..." : "Save Changes"} 
            variant="dark" 
            onClick={handleSave}
            className={saving ? "opacity-75 cursor-not-allowed" : ""}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default Settings;